<script setup lang="ts">
import { nextTick, ref } from 'vue'
import AppPopover from '../AppPopover/AppPopover.vue'
import type { AnchorPlacement } from '../AppPopover/use-anchored-position'
import type { MenuItem } from './menu-types'

const open = defineModel<boolean>('open', { default: false })

withDefaults(
  defineProps<{
    items?: MenuItem[]
    placement?: AnchorPlacement
    offset?: number
  }>(),
  {
    items: () => [],
    placement: 'bottom-start',
    offset: 8,
  },
)

const emit = defineEmits<{
  select: [id: string]
}>()

const menuRef = ref<HTMLElement | null>(null)
const activeIndex = ref(0)

function menuItems(): HTMLElement[] {
  if (!menuRef.value) return []
  return Array.from(menuRef.value.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
    (el) => el.getAttribute('aria-disabled') !== 'true' && !el.hasAttribute('disabled'),
  )
}

function focusIndex(index: number) {
  const items = menuItems()
  if (items.length === 0) return
  const next = ((index % items.length) + items.length) % items.length
  items.forEach((el, i) => {
    el.tabIndex = i === next ? 0 : -1
  })
  activeIndex.value = next
  items[next]!.focus()
}

function onOpened() {
  nextTick(() => focusIndex(0))
}

function onSelect(item: MenuItem) {
  if (item.disabled) return
  emit('select', item.id)
  open.value = false
}

function onKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusIndex(activeIndex.value + 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      focusIndex(activeIndex.value - 1)
      break
    case 'Home':
      event.preventDefault()
      focusIndex(0)
      break
    case 'End':
      event.preventDefault()
      focusIndex(menuItems().length - 1)
      break
    case 'Tab':
      event.preventDefault()
      open.value = false
      break
  }
}
</script>

<template>
  <AppPopover
    v-model:open="open"
    role="menu"
    aria-haspopup="menu"
    :placement="placement"
    :offset="offset"
    :trap-focus="false"
    :auto-focus="false"
    @opened="onOpened"
  >
    <template #trigger="triggerProps">
      <slot name="trigger" v-bind="triggerProps" />
    </template>

    <div ref="menuRef" role="presentation" class="flex flex-col gap-1 p-4" @keydown="onKeydown">
      <template v-if="$slots.header">
        <slot name="header" />
        <div role="separator" class="-mx-4 h-px shrink-0 bg-border" />
      </template>
      <slot>
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          role="menuitem"
          tabindex="-1"
          :disabled="item.disabled"
          :class="[
            'flex w-full items-center gap-6 rounded-2 px-6 py-4 text-left text-body-sm',
            'outline-none transition-colors duration-fast ease-soft motion-reduce:transition-none',
            item.disabled
              ? 'cursor-not-allowed text-text-tertiary opacity-60'
              : item.danger
                ? 'cursor-pointer text-accent-red hover:bg-accent-red/10 focus-visible:bg-accent-red/10'
                : 'cursor-pointer text-text-primary hover:bg-bg-tertiary focus-visible:bg-bg-tertiary',
          ]"
          @click="onSelect(item)"
        >
          <component :is="item.icon" v-if="item.icon" class="shrink-0" />
          <span class="flex min-w-0 grow flex-col">
            <span>{{ item.label }}</span>
            <span v-if="item.caption" class="text-caption text-text-tertiary">{{
              item.caption
            }}</span>
          </span>
        </button>
      </slot>
    </div>
  </AppPopover>
</template>
